/**
 * weather-favorites.ts — gestion des villes météo mémorisées
 *
 * Les favoris sont stockés dans `board.settings.weatherFavorites` (JSON).
 * Ce module contient uniquement des fonctions pures sur les tableaux de favoris,
 * sans dépendance réseau ni React — entièrement testables.
 */

import type { WeatherLayout, WeatherStyle } from "@mem/shared";

// ─── Types ────────────────────────────────────────────────────────

export interface WeatherFavorite {
  /** Identifiant unique généré côté client (crypto.randomUUID). */
  id: string;
  city_label: string;
  country_code?: string;
  /** Requête geocode originale, pour affichage. */
  city_query: string;
  latitude: number;
  longitude: number;
  timezone: string;
  layout: WeatherLayout;
  style: WeatherStyle;
}

// ─── Identification d'un favori ───────────────────────────────────

/**
 * Clé de déduplication : arrondit lat/lon à 2 décimales (~1km de précision).
 * Deux cartes pour la même ville (même coords) avec le même layout+style
 * sont considérées identiques.
 */
export function favoriteKey(
  lat: number,
  lon: number,
  layout: WeatherLayout,
  style: WeatherStyle,
): string {
  return `${Math.round(lat * 100) / 100},${Math.round(lon * 100) / 100},${layout},${style}`;
}

function favKey(f: WeatherFavorite): string {
  return favoriteKey(f.latitude, f.longitude, f.layout, f.style);
}

/**
 * Vérifie si un favori correspondant à ces paramètres existe déjà dans la liste.
 */
export function isFavorited(
  favorites: WeatherFavorite[],
  lat: number,
  lon: number,
  layout: WeatherLayout,
  style: WeatherStyle,
): boolean {
  const key = favoriteKey(lat, lon, layout, style);
  return favorites.some((f) => favKey(f) === key);
}

/**
 * Retourne l'id du favori correspondant, ou undefined s'il n'existe pas.
 */
export function findFavoriteId(
  favorites: WeatherFavorite[],
  lat: number,
  lon: number,
  layout: WeatherLayout,
  style: WeatherStyle,
): string | undefined {
  const key = favoriteKey(lat, lon, layout, style);
  return favorites.find((f) => favKey(f) === key)?.id;
}

// ─── Mutations sur le tableau ─────────────────────────────────────

/** Ajoute un favori (dédupliqué par lat/lon/layout/style). */
export function addFavorite(
  favorites: WeatherFavorite[],
  fav: WeatherFavorite,
): WeatherFavorite[] {
  if (isFavorited(favorites, fav.latitude, fav.longitude, fav.layout, fav.style)) {
    return favorites; // déjà présent — idempotent
  }
  return [...favorites, fav];
}

/** Retire un favori par son id. */
export function removeFavorite(
  favorites: WeatherFavorite[],
  id: string,
): WeatherFavorite[] {
  return favorites.filter((f) => f.id !== id);
}

/** Extrait les favoris depuis le blob settings du board. */
export function getFavoritesFromSettings(
  settings: Record<string, unknown> | undefined | null,
): WeatherFavorite[] {
  if (!settings) return [];
  const raw = settings.weatherFavorites;
  if (!Array.isArray(raw)) return [];
  // Filtre les entrées malformées (migration défensive)
  return raw.filter(
    (f): f is WeatherFavorite =>
      typeof f === "object" &&
      f !== null &&
      typeof (f as WeatherFavorite).id === "string" &&
      typeof (f as WeatherFavorite).city_label === "string" &&
      typeof (f as WeatherFavorite).latitude === "number" &&
      typeof (f as WeatherFavorite).longitude === "number",
  );
}
